import { ManageMetadataEditDrawer } from '@modules/integrationsV2/components/ManageMetadataAccordion/ManageMetadataEditDrawer';
import {
  useFetchCoverageForMultipleMetadataFields,
  useFetchMetadataDetailsForEqlSource,
  useGetManageMetadataTableColumns,
} from '@modules/integrationsV2/hooks';
import { useManageMetadataEditDrawerPagination } from '@modules/integrationsV2/hooks/useManageMetadataEditDrawerPagination';
import {
  IntegrationSource,
  ManageMetadataTableRow,
} from '@modules/integrationsV2/types';
import {
  mapIntegrationTypeToObjectType,
  trackIntegrationPageManageMetadataEvent,
} from '@modules/integrationsV2/utils';
import { EditUnifiedFieldDrawer } from '@modules/ManageUnifiedFields/components/EditUnifiedFieldDrawer';
import { SearchRounded } from '@mui/icons-material';
import { InputAdornment, Stack, TextField } from '@mui/material';
import {
  DataGridPro,
  gridFilteredSortedRowIdsSelector,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { IntegrationType } from 'generated/graphql';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';

const PAGE_SIZE = 8;
interface ManageMetadataTableProps {
  integrationSource: IntegrationSource;
}

export const ManageMetadataTable = ({
  integrationSource,
}: ManageMetadataTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const { metadataDetails, isLoading } = useFetchMetadataDetailsForEqlSource({
    eqlSource: integrationSource.source ?? '',
    objectType: mapIntegrationTypeToObjectType(
      integrationSource.integrationType,
    ),
  });

  const filteredAndSortedMetadataDetails = useMemo(() => {
    if (!searchQuery) {
      return metadataDetails;
    }

    const filteredRows = metadataDetails.filter(
      (metadata) =>
        metadata.displayText
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        metadata.originalDisplayText
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
    );

    // Higher priority for 'displayText' match over 'originalDisplayText'
    return orderBy(filteredRows, (metadata) =>
      metadata.displayText.toLowerCase().includes(searchQuery.toLowerCase())
        ? 0
        : 1,
    );
  }, [metadataDetails, searchQuery]);

  const pageRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;

    const sortedRows =
      sortModel && !!sortModel[0]?.sort
        ? orderBy(
            filteredAndSortedMetadataDetails,
            sortModel[0].field,
            sortModel[0].sort,
          )
        : filteredAndSortedMetadataDetails;

    return sortedRows.slice(start, end);
  }, [
    filteredAndSortedMetadataDetails,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
  ]);

  const { valuesMap } = useFetchCoverageForMultipleMetadataFields({
    metadataFields: pageRows,
    metadataIntegrationType: integrationSource.integrationType,
  });

  const mappedTableRows = useMemo((): ManageMetadataTableRow[] => {
    return filteredAndSortedMetadataDetails.map((metadata) => ({
      ...metadata,
      coverage: valuesMap[metadata.key],
    }));
  }, [filteredAndSortedMetadataDetails, valuesMap]);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isEditUnifiedFieldDrawerOpen, setIsEditUnifiedFieldDrawerOpen] =
    useState(false);
  const [selectedUnifiedFieldKeyForEdit, setSelectedUnifiedFieldKeyForEdit] =
    useState<string | null>(null);

  const {
    onMetadataToEditChange,
    onCancel,
    onRowsChange,
    onNext,
    onPrevious,
    metadataToEdit,
    currentFieldIndex,
  } = useManageMetadataEditDrawerPagination({
    metadataData: mappedTableRows,
  });

  const onEditButtonClick = (metadata: ManageMetadataTableRow) => {
    setIsEditDrawerOpen(true);
    onMetadataToEditChange(metadata);
    trackIntegrationPageManageMetadataEvent({
      eventName: 'Manage Metadata Table - Edit Metadata Button Clicked',
      integration: {
        channelType: integrationSource.channel.type,
        integrationType: integrationSource.integrationType,
        namespace: integrationSource.namespace,
      },
    });
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    onCancel();
  };

  const isStarringSupported =
    integrationSource.integrationType === IntegrationType.Feedback;

  const openEditUnifiedFieldDrawer = (metadataKey?: string) => {
    if (!metadataKey) {
      return;
    }

    setIsEditUnifiedFieldDrawerOpen(true);
    setSelectedUnifiedFieldKeyForEdit(metadataKey);
  };

  const columns = useGetManageMetadataTableColumns({
    onEditButtonClick,
    shouldShowStarredColumn: isStarringSupported,
    onEditUnifiedFieldButtonClick: openEditUnifiedFieldDrawer,
    integrationSource,
  });

  const getNoRowsOverlayText = () => {
    if (searchQuery) {
      return `No matches found for "${searchQuery}"`;
    }

    return `No metadata fields found for ${
      integrationSource.channel.displayName
    } ${
      integrationSource.namespace ? ` - ${integrationSource.namespace}` : ''
    }`;
  };

  const shouldHideFooter = mappedTableRows.length === 0;

  return (
    <Stack>
      <TextField
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          trackIntegrationPageManageMetadataEvent({
            eventName: 'Manage Metadata Table - Search Operation Performed',
            integration: {
              channelType: integrationSource.channel.type,
              integrationType: integrationSource.integrationType,
              namespace: integrationSource.namespace,
            },
          });
        }}
        placeholder="Search"
        fullWidth
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded
                sx={{
                  color: 'text.secondary',
                }}
              />
            </InputAdornment>
          ),
        }}
      />
      <DataGridPro
        sx={{
          border: 'none',
          '& .MuiTablePagination-displayedRows': {
            marginTop: '1rem', // to align pagination text
          },
          '& .MuiDataGrid-row': {
            position: 'relative',
            '&: hover': {
              cursor: 'pointer',
              '.js-manage-metadata-row-action-button-group': {
                opacity: 1,
              },
            },
          },
        }}
        loading={isLoading}
        columns={columns}
        rows={mappedTableRows}
        pagination
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } },
        }}
        hideFooter={shouldHideFooter}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[PAGE_SIZE]}
        getRowId={({ key }) => key}
        autoHeight
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 40}
        columnHeaderHeight={45}
        disableRowSelectionOnClick
        disableColumnMenu
        onRowClick={(params) => onEditButtonClick(params.row)}
        slots={{
          noRowsOverlay: () =>
            !isLoading ? (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {getNoRowsOverlayText()}
              </Stack>
            ) : null,
        }}
        onStateChange={(state) => {
          const rowIDs = gridFilteredSortedRowIdsSelector(state).map((id) =>
            id.toString(),
          );
          onRowsChange(rowIDs);
        }}
      />
      <ManageMetadataEditDrawer
        metadataToEdit={metadataToEdit}
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        onNext={onNext}
        onPrevious={onPrevious}
        currentFieldIndex={currentFieldIndex}
        totalFieldCount={metadataDetails.length}
        integrationSource={integrationSource}
        shouldShowStarredField={isStarringSupported}
      />
      {selectedUnifiedFieldKeyForEdit && (
        <EditUnifiedFieldDrawer
          open={isEditUnifiedFieldDrawerOpen}
          onClose={() => {
            setIsEditUnifiedFieldDrawerOpen(false);
            setSelectedUnifiedFieldKeyForEdit(null);
          }}
          metadataKey={selectedUnifiedFieldKeyForEdit ?? ''}
        />
      )}
    </Stack>
  );
};
